import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Loader2, Newspaper } from 'lucide-react';
import { fetchPublishedPosts } from '../lib/db';
import { useSeo } from '../lib/seo';
import type { Post } from '../lib/types';

export default function News() {
  useSeo({
    title: 'News & Stories | Kisapuk Community Initiative',
    description: 'Read the latest updates, stories, and community impact from Kisapuk Community Initiative.',
    pathname: '/news',
    image: 'https://your-domain.com/news-social.png',
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedPosts('news')
      .then((data) => {
        setPosts(data);
        setLoading(false);
      })
      .catch(() => {
        setPosts([]);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <section className="bg-brand-950 py-24 text-white">
        <div className="container-prose">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-brand-100 backdrop-blur-sm">
            <Newspaper className="h-4 w-4" />
            Latest Updates
          </div>
          <h1 className="mt-6 font-display text-4xl font-bold sm:text-5xl">News & Stories</h1>
          <p className="mt-4 max-w-2xl text-lg text-brand-100">
            Follow the latest work, community stories, and progress from Kisapuk Community Initiative.
          </p>
        </div>
      </section>

      <section className="container-prose py-16 lg:py-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : posts.length === 0 ? (
          <div className="card p-8 text-center">
            <h2 className="font-display text-2xl font-semibold text-stone-900">No news posts yet</h2>
            <p className="mt-3 text-stone-600">Fresh stories will appear here soon.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => (
              <Link key={post.id} to={`/programs/${post.slug}`} className="card overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="relative h-48 overflow-hidden bg-stone-200">
                  {post.cover_image_url ? (
                    <img src={post.cover_image_url} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-brand-100 text-brand-700">News Feature</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-brand-600">
                    <Calendar className="h-4 w-4" />
                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <h3 className="mt-3 font-display text-xl font-semibold text-stone-900">{post.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{post.excerpt}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-brand-700">
                    Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
